import React from 'react';
import { Dayjs } from 'dayjs';
import { TablePaginationConfig } from 'antd/es/table';
import {
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { RcFile } from 'antd/lib/upload';

export interface ResourceBase {
  id: number;
  time_added: string;
  time_modified?: string;
}

type FieldNamePath = string | number | (string | number)[];
// internally used by Antd, from rc-field-form. Doesn't seem to be exported though
export interface FieldData {
  touched?: boolean;
  validating?: boolean;
  errors?: string[];
  name: FieldNamePath;
  value: any;
}

export type DateRange = [Dayjs | null, Dayjs | null] | null;

export type Channel = 'ipad' | 'shop' | undefined;

export interface TableParams<T extends {}> {
  search?: string;
  filters?: Record<string, any | null>;
  sorter?: SorterResult<T> | SorterResult<T>[];
  pagination?: TablePaginationConfig;
  customFilters?: CustomTableFilterChangePayload;
}

export type TableFilters = Record<string, FilterValue | null>;

export type HandleTableChange<T> = (
  pagination: TablePaginationConfig,
  filters: TableFilters,
  sorter: SorterResult<T> | SorterResult<T>[],
  extra: TableCurrentDataSource<T>
) => void;

export enum JobState {
  Created = 'created',
  Queued = 'queued',
  InProgress = 'in_progress',
  Completed = 'completed',
  Failed = 'failed',
  Reverted = 'reverted',
  RevertError = 'revert_error',
  Reverting = 'reverting',
}

export enum JobType {
  TaxChange = 'tax_change',
  ProductsImport = 'product_import',
  GiftcardsImport = 'giftcards_import',
  CustomersExport = 'customer_export',
  CustomersImport = 'customer_import',
  StockExport = 'stock_export',
  StockImport = 'stock_import',
  BatchZipReport = 'batch_zip_report',
  KSVProvisioning = 'ksv_provisioning',
  GiftCardReport = 'giftcards_export',
}

export interface GetJobsFilters {
  limit?: number;
  type?: JobType;
  state?: JobState;
  type__in?: JobType[];
  state__in?: JobState[];
}

export interface Job<
  Arguments = unknown,
  Result = unknown,
> extends ResourceBase {
  type: JobType;
  type_display: string;
  initiator: number | null;
  progress: unknown;
  state: JobState;
  result: Result;
  state_display: string;
  arguments: Arguments;
}

export interface JobInProgressEv<Arguments = unknown> extends ResourceBase {
  type: JobType;
  state: JobState.Created | JobState.Queued | JobState.InProgress;
  result: null;
  arguments: Arguments;
}

export interface JobSuccessEv<
  Arguments = unknown,
  Result = unknown,
> extends ResourceBase {
  type: JobType;
  state: JobState.Completed;
  result: Result;
  arguments: Arguments;
}

export interface JobFailedEv<
  Arguments = unknown,
  Result = unknown,
> extends ResourceBase {
  type: JobType;
  state: JobState.Failed;
  result: Result;
  arguments: Arguments;
}

export type JobEventBase = JobInProgressEv | JobSuccessEv | JobFailedEv;

export type SocketIOMsgType = 'event' | 'entity';
export interface SocketIoMsgSchemaBase {
  account: number;
  rid: string;
  scope: string;
  ts: string;
  user: number;
  _class: SocketIOMsgType;
}

export interface SocketIoEventMsg<
  Arguments extends unknown = null,
  Result extends unknown = null,
> extends SocketIoMsgSchemaBase {
  name: string;
  fields: {
    id: number;
    result: Result;
    arguments: Arguments;
    status:
      | JobState.Failed
      | JobState.Completed
      | JobState.Created
      | JobState.Queued
      | JobState.InProgress;
    type:
      | JobType.BatchZipReport
      | JobType.ProductsImport
      | JobType.GiftcardsImport
      | JobType.StockExport
      | JobType.StockImport
      | JobType.KSVProvisioning
      | JobType.TaxChange
      | JobType.GiftCardReport;
  };
}

export interface SocketIoEntityMsg extends SocketIoMsgSchemaBase {
  id: number;
  entity: string;
  source: string;
  state: string;
}

export interface Address {
  id: number;
  country: string | null;
  address1: string | null;
  address2: string | null;
  zipcode: string | null;
  city: string | null;
  state: string | null;
  first_name: string | null;
  last_name: string | null;
  company: string | null;
  title: string | null;
}

export interface SelectOption {
  key?: string;
  label: React.ReactNode;
  value: string | number;
}

export type UploadingFile = RcFile | Blob | string;

export type Modify<T, R> = Omit<T, keyof R> & R;

export interface CustomTableFilterState {
  contains?: string;
  isEmpty?: boolean;
  isEqual?: string;
  list?: string;
}

export interface CustomTableFilterOptions {
  useOnlyName?: boolean;
}

type CustomTableFilterChangePayload = Record<
  string,
  CustomTableFilterState & { options?: CustomTableFilterOptions }
>;
export type HandleCustomTableFilterChange = (
  change: CustomTableFilterChangePayload
) => void;

export type SupportedLanguage = 'DE' | 'EN' | 'ES' | 'ZU';

export const jobWaitingStates = [
  JobState.Created,
  JobState.Queued,
  JobState.InProgress,
];

export interface APIError extends Error {
  statusCode?: number;
  key?: string;
}

export const coreApiImportExportStatus = {
  0: 'Done',
  1: 'Parser OK',
  2: 'Import',
  10: 'File error',
  11: 'Parser error',
  12: 'Import error',
  13: 'Reverted',
  14: 'Reverting',
  15: 'Cancelled',
  16: 'Revert error',
  100: 'Queue',
  500: 'Unknown error',
};

export type ImportExportStatusCode = keyof typeof coreApiImportExportStatus;

export interface ImportExportErrorDetail {
  fields: Record<string, string>;
  description: string | null;
  key: string;
}

export interface PusherImportRecord {
  account: null;
  name: string;
  fields: Fields;
  ts: string;
  user: null;
  scope: string;
  rid: null;
  _class: string;
}

export interface Fields {
  status: Status;
  job_id: number;
  type: string;
}

export interface Status {
  id: number;
  status_code: number;
  status_details: null;
}
export interface PusherImportCompletedRecord extends Omit<
  PusherImportRecord,
  'fields'
> {
  fields: JobFields;
}

export interface JobFields {
  status: string;
  arguments: Arguments;
  type: string;
  id: number;
  result: Result;
}

export interface Arguments {
  source_file: string;
  with_drop: boolean;
}

export interface Result {
  error: ImportExportErrorDetail | null;
  data: Data;
  resource_id: number;
}

export interface Data {
  file_url: string;
}
